<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up" style="width:600px">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h2 class="pg-modal-title">{{ goal ? 'Personal Goal' : 'New Personal Goal' }}</h2>

            <div class="pg-label">Goal</div>
            <input type="text" v-model="form.Name" placeholder="e.g. Finish my CPA, coach my son's team, pay off the truck" ref="nameInput" />

            <div class="pg-label">Kind of Goal</div>
            <div class="pg-chips">
                <button v-for="c in categories" :key="c.value" type="button" class="pg-chip" :class="form.Category == c.value ? 'on' : ''" @click="form.Category = c.value">{{ c.label }}</button>
            </div>

            <div class="pg-label">Why It Matters <span class="pg-hint">(optional)</span></div>
            <input type="text" v-model="form.Why" placeholder="One line you'll read when it gets hard" />

            <div class="pg-row">
                <div>
                    <div class="pg-label">Done By</div>
                    <input type="date" v-model="form.DueDate" />
                </div>
                <div>
                    <div class="pg-label">How You'll Track It</div>
                    <div class="pg-seg">
                        <button v-for="t in trackTypes" :key="t.value" type="button" :class="form.TrackType == t.value ? 'on' : ''" @click="setTrackType(t.value)">{{ t.label }}</button>
                    </div>
                </div>
            </div>

            <template v-if="form.TrackType == 'steps'">
                <div v-for="(s, i) in form.Steps" :key="i" class="pg-step-row">
                    <input type="text" v-model="s.Text" :placeholder="'Step ' + (i + 1)" :ref="'step' + i" @keydown.enter.prevent="addStep()" />
                    <span class="pg-x" title="Remove step" @click="form.Steps.splice(i, 1)"><svg-icon icon="x" /></span>
                </div>
                <div class="pg-add-step" @click="addStep()">+ Add Step</div>
            </template>
            <div v-else-if="form.TrackType == 'number'" class="pg-number-row">
                <span>From</span>
                <input type="number" v-model="form.StartValue" aria-label="Start" />
                <span>to</span>
                <input type="number" v-model="form.TargetValue" placeholder="12" aria-label="Target" />
                <input type="text" v-model="form.Unit" placeholder="books, lbs, $…" aria-label="Unit" class="pg-unit" />
            </div>
            <p v-if="goal && form.TrackType != goal.TrackType" class="pg-hint" style="margin:8px 0 0">Changing how it's tracked starts its progress over.</p>

            <div class="pg-label">Who Can See It</div>
            <div class="pg-seg">
                <button v-for="v in visibilities" :key="v.value" type="button" :class="form.Visibility == v.value ? 'on' : ''" @click="form.Visibility = v.value">{{ v.label }}</button>
            </div>
            <p class="pg-hint" style="margin:8px 0 0">{{ visibilityHint }}</p>

            <div class="pg-actions">
                <a v-if="goal" class="pg-danger" @click.prevent="showConfirmDelete = true">Delete</a>
                <span style="flex:1"></span>
                <div class="btn-light" @click="$emit('close')">Cancel</div>
                <div class="btn" :class="saving ? 'disable-button' : ''" @click="save()">{{ goal ? 'Save' : 'Add Goal' }}</div>
            </div>
        </div>

        <confirm-delete v-if="showConfirmDelete" @confirm="remove()" @cancel="showConfirmDelete = false" msg="Delete this goal and its check-ins?" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import ConfirmDelete from './ConfirmDelete.vue';
import SvgIcon from './SvgIcon.vue';
import { CATEGORIES, TRACK_TYPES, VISIBILITIES, isoDate, addDays } from '@/personalGoals';

export default defineComponent({
    name: 'PersonalGoalModal',
    // goal: one of your goals to edit, or null to add one
    props: [ 'goal' ],
    emits: [ 'close', 'saved' ],
    components: {
        ConfirmDelete,
        SvgIcon
    },
    data() {
        return {
            categories: CATEGORIES,
            trackTypes: TRACK_TYPES,
            visibilities: VISIBILITIES,
            form: {} as any,
            saving: false,
            showConfirmDelete: false
        }
    },
    created() {
        const g = this.goal;
        this.form = g ? {
            Name: g.Name,
            Category: g.Category,
            Why: g.Why,
            DueDate: g.DueDate || '',
            TrackType: g.TrackType,
            Steps: g.Steps.map((s: any) => ({ ...s })),
            StartValue: g.StartValue ?? 0,
            TargetValue: g.TargetValue ?? '',
            Unit: g.Unit,
            Visibility: g.Visibility
        } : {
            Name: '',
            Category: 'career',
            Why: '',
            DueDate: addDays(isoDate(new Date()), 90),
            TrackType: 'steps',
            Steps: [ { Text: '', Done: false } ],
            StartValue: 0,
            TargetValue: '',
            Unit: '',
            Visibility: 'manager'
        };
    },
    mounted() {
        if(!this.goal) {
            (this.$refs.nameInput as HTMLInputElement)?.focus();
        }
    },
    computed: {
        visibilityHint(): string {
            return ({
                private: 'Nobody else sees it.',
                manager: 'Your manager and Team Hub Admins can see it.',
                team: 'Your manager, Team Hub Admins, and everyone on your team can see it.'
            } as any)[this.form.Visibility];
        }
    },
    methods: {
        setTrackType(type: string) {
            this.form.TrackType = type;
            if(type == 'steps' && !this.form.Steps.length) {
                this.form.Steps.push({ Text: '', Done: false });
            }
        },
        addStep() {
            this.form.Steps.push({ Text: '', Done: false });
            this.$nextTick(() => {
                const ref = this.$refs['step' + (this.form.Steps.length - 1)] as any;
                (Array.isArray(ref) ? ref[0] : ref)?.focus();
            });
        },
        async save() {
            if(this.saving) {
                return;
            }
            if(!this.form.Name.trim()) {
                alert('Write the goal first.');
                return;
            }
            if(this.form.TrackType == 'number' && (this.form.TargetValue === '' || this.form.TargetValue === null)) {
                alert('Set the number you\'re aiming for.');
                return;
            }
            this.saving = true;
            await axios.post(`${this.$store.state.apiUrl}/api/personalgoals/goal`, { ...this.form, ID: this.goal ? this.goal.ID : null })
                .then(() => {
                    this.$emit('saved');
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.saving = false;
        },
        async remove() {
            this.showConfirmDelete = false;
            await axios.post(`${this.$store.state.apiUrl}/api/personalgoals/goal/delete`, { ID: this.goal.ID })
                .then(() => {
                    this.$emit('saved');
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
        }
    }
});
</script>
